import { faChevronLeft, faInfoCircle } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@shared/components/ui/button';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { forwardRef, useImperativeHandle, useRef } from 'react';
import { useTranslation } from 'react-i18next';

export interface NoShippingErrorModalRef {
  openModal: (orderId: number) => void;
  closeModal: () => void;
}

interface NoShippingErrorModalProps {
  onSuccess?: () => void;
}

export const NoShippingErrorModal = forwardRef<NoShippingErrorModalRef, NoShippingErrorModalProps>(
  ({ onSuccess }, ref) => {
    const { t } = useTranslation('viewOrdersModule');
    const modalRef = useRef<ModalCenterRef>(null);

    useImperativeHandle(ref, () => ({
      openModal: (_orderId: number) => {
        modalRef.current?.openModal();
      },
      closeModal: () => modalRef.current?.closeModal(),
    }));

    const handleClose = () => {
      modalRef.current?.closeModal();
    };

    const handleBack = () => {
      modalRef.current?.closeModal();
    };

    return (
      <ModalCenter
        ref={modalRef}
        title={t('noShippingError.title')}
        icon={
          <button
            onClick={handleBack}
            className="flex items-center justify-center w-6 h-6 hover:bg-hover-press rounded transition-colors"
          >
            <FontAwesomeIcon icon={faChevronLeft} className="w-4 h-4" />
          </button>
        }
        type="default"
        fullScreenMobile={true}
        width="lg"
        className="max-h-[90vh]"
      >
        <div className="flex flex-col h-full">
          <div className="flex-1 space-y-4 mt-2 pb-24 md:pb-0 max-h-[70vh] overflow-y-auto">
            <p className="text-sm">{t('noShippingError.description')}</p>

            <div className="border border-warning dark:border-[#4C4C07] bg-bg-warning dark:bg-[#281B13] rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 h-8">
                  <div className="w-8 h-8 rounded-full bg-warning flex items-center justify-center">
                    <FontAwesomeIcon icon={faInfoCircle} className="text-white light:!text-white w-4 h-4" />
                  </div>
                </div>
                <div className="flex-1">
                  <div className="text-warning dark:text-warning text-sm font-medium">
                    {t('noShippingError.infoMessage')}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="fixed bottom-0 left-0 right-0 p-4 md:pb-0 md:px-0 md:relative md:border-t-0">
            <div className="flex justify-end gap-3">
              <Button variant="primary" onClick={handleClose} size="lg">
                {t('noShippingError.close')}
              </Button>
            </div>
          </div>
        </div>
      </ModalCenter>
    );
  },
);
